import { memo } from "react";
import { Box, Typography, Stack } from "@mui/material";
import { IShipmentByStatus } from "../../../interfaces/dashboard";
import { formatNumber, formatRate } from "../../../utils/metricsFormatters";
import {
  STATUS_TRANSLATOR,
  PENDING,
  IN_PROCESS,
  IN_TRANSIT,
  RESCHEDULED,
  SHIPMENT_TEMPORARY_IN_BRANCH_OFFICE,
  SHIPMENT_TEMPORARY_IN_BRANCH_OFFICE_TO_WITHDRAW,
} from "../../../utils/constants";

interface IShipmentsByStatusProps {
  data: IShipmentByStatus[];
}

// Color por estado (según el modelo de Figma). El indicador es neutral/semántico
// —no una señal de "negatividad"—: azules para el flujo activo, verde para "en
// camino", ámbar para reprogramado, gris para las esperas en sucursal.
const NEUTRAL = "#90a4ae";
const STATUS_COLOR: Record<string, string> = {
  [PENDING]: "#1e88e5",
  [IN_PROCESS]: "#1565c0",
  [IN_TRANSIT]: "#2e7d32",
  [RESCHEDULED]: "#f9a825",
  [SHIPMENT_TEMPORARY_IN_BRANCH_OFFICE]: NEUTRAL,
  [SHIPMENT_TEMPORARY_IN_BRANCH_OFFICE_TO_WITHDRAW]: NEUTRAL,
};

const statusColor = (status: string): string => STATUS_COLOR[status] ?? NEUTRAL;

// El backend ya devuelve `label` traducido (fuente de verdad). Se mantiene el
// fallback a STATUS_TRANSLATOR (y al key crudo) por si convive un backend
// previo sin el campo.
const statusLabel = (item: IShipmentByStatus): string =>
  item.label || STATUS_TRANSLATOR[item.status] || item.status;

const ShipmentsByStatus = ({ data }: IShipmentsByStatusProps) => (
  <Stack divider={<Box sx={{ borderBottom: "1px solid #f0f0f0" }} />}>
    {data.map((item) => (
      <Box
        key={item.status}
        display="flex"
        alignItems="center"
        gap={1.5}
        py={1}
      >
        {/* Barra vertical de color: indicador de estado, no de sentimiento. */}
        <Box
          sx={{
            width: 4,
            height: 20,
            borderRadius: 2,
            backgroundColor: statusColor(item.status),
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" color="text.primary" sx={{ flex: 1 }} noWrap>
          {statusLabel(item)}
        </Typography>
        <Typography variant="body2" fontWeight={700} color="text.primary">
          {formatNumber(item.count)}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: 44, textAlign: "right" }}
        >
          {formatRate(item.percentage)}
        </Typography>
      </Box>
    ))}
  </Stack>
);

export default memo(ShipmentsByStatus);
